"use client"

import { useState } from "react"
import {
  Download,
  Loader2,
  CheckCircle2,
  User,
  Building2,
  Mail,
} from "lucide-react"

interface GlossaryDownloadFormProps {
  compact?: boolean
}

export default function GlossaryDownloadForm({
  compact = false,
}: GlossaryDownloadFormProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [form, setForm] = useState({
    nombre: "",
    apellidos: "",
    empresa: "",
    email: "",
    acepta_privacidad: false,
    acepta_novedades: false,
  })
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (!form.nombre.trim() || !form.apellidos.trim() || !form.email.trim()) {
      setError("Nombre, apellidos y email son obligatorios")
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(form.email)) {
      setError("Introduce un email válido")
      return
    }

    if (!form.acepta_privacidad) {
      setError("Debes aceptar la política de privacidad")
      return
    }

    setLoading(true)
    try {
      await fetch("/api/glosario/download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          recurso: "diccionario-hosteleria-pdf",
        }),
      })

      setDone(true)
      const link = document.createElement("a")
      link.href = "/glosario/downloads/diccionario-hosteleria.pdf"
      link.download = ""
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      setTimeout(() => {
        setDone(false)
        setIsOpen(false)
      }, 3000)
    } catch {
      const link = document.createElement("a")
      link.href = "/glosario/downloads/diccionario-hosteleria.pdf"
      link.download = ""
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      setIsOpen(false)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`inline-flex items-center justify-center gap-2 bg-gradient-to-r from-lime-500 to-lime-600 text-white rounded-xl font-semibold transition-all hover:opacity-90 group ${
          compact ? "px-6 py-3 text-base" : "px-8 py-4 text-lg"
        }`}
      >
        <Download className="w-5 h-5" />
        {compact ? "Descargar PDF" : "Descargar diccionario en PDF"}
      </button>
    )
  }

  return (
    <div className="w-full max-w-md">
      <div className="bg-white rounded-2xl border border-zinc-200 shadow-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-lime-500 to-lime-600 flex items-center justify-center">
            <Download className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-zinc-900 text-sm">
              Diccionario de Hostelería
            </h3>
            <p className="text-xs text-zinc-500">
              Rellena tus datos para descargar el PDF
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <input
                type="text"
                placeholder="Nombre *"
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="text"
                placeholder="Apellidos *"
                value={form.apellidos}
                onChange={(e) =>
                  setForm({ ...form, apellidos: e.target.value })
                }
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="text"
              placeholder="Nombre de empresa"
              value={form.empresa}
              onChange={(e) => setForm({ ...form, empresa: e.target.value })}
              className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              type="email"
              placeholder="Email profesional *"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-lime-500 focus:border-transparent"
            />
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={form.acepta_privacidad}
              onChange={(e) =>
                setForm({ ...form, acepta_privacidad: e.target.checked })
              }
              className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-lime-600 focus:ring-lime-500 accent-lime-600"
            />
            <span className="text-xs text-zinc-500 leading-relaxed">
              He leído y acepto la{" "}
              <a
                href="/legal/privacidad"
                target="_blank"
                className="text-lime-600 underline hover:text-lime-700"
              >
                política de privacidad
              </a>
              . *
            </span>
          </label>

          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={form.acepta_novedades}
              onChange={(e) =>
                setForm({ ...form, acepta_novedades: e.target.checked })
              }
              className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-lime-600 focus:ring-lime-500 accent-lime-600"
            />
            <span className="text-xs text-zinc-500 leading-relaxed">
              Acepto recibir novedades y consejos para mi restaurante por email.
            </span>
          </label>

          {error && <p className="text-sm text-red-500">{error}</p>}

          <button
            type="submit"
            disabled={loading || done}
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-lime-500 to-lime-600 text-white px-6 py-3 rounded-xl font-semibold transition-all hover:opacity-90 disabled:opacity-70"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Procesando...
              </>
            ) : done ? (
              <>
                <CheckCircle2 className="w-5 h-5" />
                ¡Descarga iniciada!
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Descargar ahora
              </>
            )}
          </button>

          <p className="text-xs text-zinc-400 text-center">
            Sin spam. Tus datos están seguros.
          </p>
        </form>
      </div>
    </div>
  )
}
